/**
 * Axios config for multipart uploads: no retries (partial body is unsafe to replay)
 * and optional onUploadProgress.
 *
 * @param {object} [options]
 * @param {number} [options.timeout]
 * @param {Function} [options.onUploadProgress]
 * @param {object} [options.headers]
 * @returns {object}
 */
export function uploadAxiosConfig({ timeout, onUploadProgress, headers, signal } = {}) {
    const config = {
        timeout,
        'axios-retry': { retries: 0 },
    };
    if (typeof onUploadProgress === 'function') {
        config.onUploadProgress = onUploadProgress;
    }
    if (headers) {
        config.headers = headers;
    }
    if (signal) {
        config.signal = signal;
    }
    return config;
}

/**
 * Last upload argument: axios callback, `{ onUploadProgress }`, or omitted.
 * @param {Function|{ onUploadProgress?: Function }|undefined} optionsOrCallback
 * @returns {Function|undefined}
 */
export function resolveOnUploadProgress(optionsOrCallback) {
    if (typeof optionsOrCallback === 'function') {
        return optionsOrCallback;
    }
    if (optionsOrCallback && typeof optionsOrCallback.onUploadProgress === 'function') {
        return optionsOrCallback.onUploadProgress;
    }
    return undefined;
}

/**
 * @param {Function|{ signal?: AbortSignal }|undefined} optionsOrCallback
 * @returns {AbortSignal|undefined}
 */
export function resolveUploadSignal(optionsOrCallback) {
    if (optionsOrCallback && typeof optionsOrCallback === 'object' && optionsOrCallback.signal) {
        return optionsOrCallback.signal;
    }
    return undefined;
}

/**
 * Axios abort / fetch AbortError — do not surface as a failed upload toast.
 * @param {unknown} error
 */
export function isUploadAbortError(error) {
    const seen = [
        error,
        error?.original,
        error?.originalError,
        error?.cause,
        error?.original?.originalError,
        error?.originalError?.originalError,
    ];
    return seen.some((item) => (
        item
        && (item.code === 'ERR_CANCELED'
            || item.name === 'CanceledError'
            || item.name === 'AbortError'
            || item.message === 'canceled')
    ));
}
